import {gql} from '@apollo/client';
import {
  PodExemptDictItemType,
  PodExemptEffectiveStatsType,
  PodExemptWaybillPreviewType,
  PodExemptNearbyItemType,
  PodExemptReportDetailType,
  PodExemptReportPageType,
  PodExemptReviewQueueStatsType,
  PodExemptDspSpotCheckPageType,
  PodExemptPodSampleType,
} from './types';

/**
 * 上报单核心字段（表内字段，任何链路都有值）。
 * mutation 只能选这一档：displayAddress 等回填字段在 mutation 链路为 null，
 * 若选上会经 Apollo 规范化缓存把列表里同一实体的回填值覆盖成 null。
 * 详情 = 核心字段 + warehouseName（详情链路后端也回填仓名，与列表口径一致）。
 */
export const POD_EXEMPT_REPORT_CORE_FIELDS = `
    id
    reportNo
    scopeType
    status
    dspId
    warehouseCode
    addressType
    reasonCodes
    reasonRemark
    zipcode
    trackingNumber
    latestRejectCodes
    latestRejectOpinion
    dspName
    creatorName
    createdAt
`;

/**
 * 列表行字段 = 核心字段 + 各端列表都回填的展示字段。
 * targetId/effectiveAt/hit30d 不在此列：它们只有「已通过的地址」链路回填，
 * 若放进通用字段集，其他列表查询返回的 null 会经规范化缓存覆盖生效页的值（同详情字段集拆分的教训）。
 */
export const POD_EXEMPT_REPORT_FIELDS = `
    ${POD_EXEMPT_REPORT_CORE_FIELDS}
    warehouseName
    displayAddress
    addressCount
`;

/** 「已通过的地址」行字段：通用列表字段 + 仅该链路回填的明细字段 */
export const POD_EXEMPT_EFFECTIVE_ROW_FIELDS = `
    ${POD_EXEMPT_REPORT_FIELDS}
    targetId
    effectiveAt
    hit30d
`;

/**
 * 审核队列行字段：通用列表字段 + 仅审核队列链路回填的材料计数/材料不全/7天单量占位。
 * 不并入通用字段集：DSP 列表链路这些字段为 null，会经规范化缓存覆盖审核队列的值（同上教训）。
 */
export const POD_EXEMPT_REVIEW_ROW_FIELDS = `
    ${POD_EXEMPT_REPORT_FIELDS}
    photoCount
    videoCount
    fileCount
    materialIncomplete
    recent7dOrderCount
`;

const REVIEW_PAGE_FIELDS = `
    content { ${POD_EXEMPT_REVIEW_ROW_FIELDS} }
    page
    size
    totalElements
    totalPages
    hasNext
    hasPrevious
`;

export const POD_EXEMPT_DETAIL_FIELDS = `
    report {
        ${POD_EXEMPT_REPORT_CORE_FIELDS}
        warehouseName
    }
    address {
        id
        rawAddress
        status
        zipcode
        latitude
        longitude
        offlineReason
        effectiveAt
    }
    fence {
        id
        name
        status
        zipcode
        pointsJson
        centerLatitude
        centerLongitude
        addressCount
        mountStatus
        effectiveAt
    }
    materials {
        materialType
        objectKey
        category
        fileName
        durationSec
        sortNo
        viewUrl
    }
    metrics {
        calcStatus
        source
        orderCnt30d
        orderCnt7d
        distinctDriverCnt
        podPhotoCnt
        podSampleOrderIds
        complaintCnt90d
        complaintCnt14d
        complaintCnt7d
        calculatedAt
    }
    flows {
        action
        operatorRole
        operatorName
        opinion
        rejectCodes
        createdAt
    }
`;

const PAGE_FIELDS = `
    content { ${POD_EXEMPT_REPORT_FIELDS} }
    page
    size
    totalElements
    totalPages
    hasNext
    hasPrevious
`;

// ---------- DSP 端 ----------

export const GET_POD_EXEMPT_WAYBILL_PREVIEW = gql`
    query PodExemptTrackingPreview($trackingNumber: String!) {
        podExemptTrackingPreview(trackingNumber: $trackingNumber) {
            rawAddress
            zipcode
            warehouseCode
            alreadyReported
            blockingReason
            reportedCount
            latestReportNo
            latestStatus
        }
    }
`;

export interface PodExemptWaybillPreviewResponse {
  podExemptTrackingPreview: PodExemptWaybillPreviewType;
}

export const GET_POD_EXEMPT_EFFECTIVE_REPORTS = gql`
    query PodExemptEffectiveReports($warehouseId: Long, $pageRequest: PageRequestTypeInput) {
        podExemptEffectiveReports(warehouseId: $warehouseId, pageRequest: $pageRequest) {
            content { ${POD_EXEMPT_EFFECTIVE_ROW_FIELDS} }
            page
            size
            totalElements
            totalPages
            hasNext
            hasPrevious
        }
    }
`;

export interface PodExemptEffectiveReportsResponse {
  podExemptEffectiveReports: PodExemptReportPageType;
}

export const GET_POD_EXEMPT_ACTIVE_FENCES = gql`
    query PodExemptActiveFences($warehouseId: Long!, $zipcode: String!) {
        podExemptActiveFences(warehouseId: $warehouseId, zipcode: $zipcode) {
            id
            name
            status
            pointsJson
        }
    }
`;

export const GET_WMS_POD_EXEMPT_ACTIVE_FENCES = gql`
    query WmsPodExemptActiveFences($reportId: Long!, $warehouseId: Long!) {
        wmsPodExemptActiveFences(reportId: $reportId, warehouseId: $warehouseId) {
            id
            name
            status
            pointsJson
        }
    }
`;

export const GET_QC_POD_EXEMPT_ACTIVE_FENCES = gql`
    query QcPodExemptActiveFences($reportId: Long!) {
        qcPodExemptActiveFences(reportId: $reportId) {
            id
            name
            status
            pointsJson
        }
    }
`;

export interface PodExemptActiveFenceItem {
  /** Long 标量为字符串 */
  id: string;
  name: string | null;
  status: string;
  pointsJson: string | null;
}

export interface PodExemptActiveFencesResponse {
  podExemptActiveFences: PodExemptActiveFenceItem[];
}

export const PRECHECK_POD_EXEMPT_FENCE = gql`
    query PodExemptFencePrecheck($warehouseId: Long!, $zipcode: String!, $points: [PodExemptPointInput!]!) {
        podExemptFencePrecheck(warehouseId: $warehouseId, zipcode: $zipcode, points: $points) {
            valid
            reason
            conflictFenceName
            clipped
            clippedPointsJson
        }
    }
`;

export interface PodExemptFencePrecheckResponse {
  podExemptFencePrecheck: {
    valid: boolean;
    reason: string | null;
    conflictFenceName: string | null;
    /** 是否发生邮编边界截断 */
    clipped: boolean;
    /** 截断后生效形状落点 JSON（pointsJson 同格式） */
    clippedPointsJson: string | null;
  };
}

export const GET_POD_EXEMPT_EFFECTIVE_STATS = gql`
    query PodExemptEffectiveStats($warehouseId: Long) {
        podExemptEffectiveStats(warehouseId: $warehouseId) {
            effectiveCount
            hit30dCount
        }
    }
`;

export interface PodExemptEffectiveStatsResponse {
  podExemptEffectiveStats: PodExemptEffectiveStatsType;
}


export const GET_POD_EXEMPT_REPORTS = gql`
    query PodExemptReports($filters: PodExemptReportFiltersInput, $pageRequest: PageRequestTypeInput) {
        podExemptReports(filters: $filters, pageRequest: $pageRequest) {
            ${PAGE_FIELDS}
        }
    }
`;

export interface PodExemptReportsResponse {
  podExemptReports: PodExemptReportPageType;
}

export const GET_POD_EXEMPT_REPORT_DETAIL = gql`
    query PodExemptReportDetail($reportId: Long!) {
        podExemptReportDetail(reportId: $reportId) {
            ${POD_EXEMPT_DETAIL_FIELDS}
        }
    }
`;

export interface PodExemptReportDetailResponse {
  podExemptReportDetail: PodExemptReportDetailType;
}

export const GET_POD_EXEMPT_SELECTABLE_ZIPCODES = gql`
    query PodExemptSelectableZipcodes($warehouseId: Long!) {
        podExemptSelectableZipcodes(warehouseId: $warehouseId)
    }
`;

export interface PodExemptSelectableZipcodesResponse {
  podExemptSelectableZipcodes: string[];
}

// ---------- 字典（三端共用；name 已按请求语言解析） ----------

export const GET_POD_EXEMPT_DICT_ITEMS = gql`
    query PodExemptDictItems($dictType: String!, $parentCode: String) {
        podExemptDictItems(dictType: $dictType, parentCode: $parentCode) {
            dictType
            code
            parentCode
            name
            nameZh
            nameEn
            nameEs
            extJson
            status
            sortNo
        }
    }
`;

export interface PodExemptDictItemsResponse {
  podExemptDictItems: PodExemptDictItemType[];
}

/** 展示映射专用：全量（含停用与已删），供历史单据回显 code→名称 */
export const GET_POD_EXEMPT_DICT_DISPLAY_ITEMS = gql`
    query PodExemptDictDisplayItems($dictType: String!) {
        podExemptDictDisplayItems(dictType: $dictType) {
            dictType
            code
            parentCode
            name
            nameZh
            nameEn
            nameEs
            extJson
            status
            sortNo
        }
    }
`;

export interface PodExemptDictDisplayItemsResponse {
  podExemptDictDisplayItems: PodExemptDictItemType[];
}

export const GET_POD_EXEMPT_DICT_ITEMS_FOR_MANAGE = gql`
    query PodExemptDictItemsForManage($dictType: String!) {
        podExemptDictItemsForManage(dictType: $dictType) {
            dictType
            code
            parentCode
            name
            nameZh
            nameEn
            nameEs
            extJson
            status
            sortNo
        }
    }
`;

export interface PodExemptDictItemsForManageResponse {
  podExemptDictItemsForManage: PodExemptDictItemType[];
}

// ---------- 仓管端 ----------

export const GET_WMS_POD_EXEMPT_REPORTS = gql`
    query WmsPodExemptReports($filters: WmsPodExemptReportFiltersInput!, $pageRequest: PageRequestTypeInput) {
        wmsPodExemptReports(filters: $filters, pageRequest: $pageRequest) {
            ${REVIEW_PAGE_FIELDS}
        }
    }
`;

export interface WmsPodExemptReportsResponse {
  wmsPodExemptReports: PodExemptReportPageType;
}

export const GET_WMS_POD_EXEMPT_REVIEW_STATS = gql`
    query WmsPodExemptReviewStats($warehouseId: Long!) {
        wmsPodExemptReviewStats(warehouseId: $warehouseId) {
            pendingCount
            overdueCount
            materialIncompleteCount
            weeklyPassRatePercent
        }
    }
`;

export interface WmsPodExemptReviewStatsResponse {
  wmsPodExemptReviewStats: PodExemptReviewQueueStatsType;
}

export const GET_WMS_POD_EXEMPT_REPORT_DETAIL = gql`
    query WmsPodExemptReportDetail($reportId: Long!, $warehouseId: Long!) {
        wmsPodExemptReportDetail(reportId: $reportId, warehouseId: $warehouseId) {
            ${POD_EXEMPT_DETAIL_FIELDS}
        }
    }
`;

export interface WmsPodExemptReportDetailResponse {
  wmsPodExemptReportDetail: PodExemptReportDetailType;
}

export const GET_WMS_POD_EXEMPT_NEARBY = gql`
    query WmsPodExemptNearbyEffective($reportId: Long!, $warehouseId: Long!) {
        wmsPodExemptNearbyEffective(reportId: $reportId, warehouseId: $warehouseId) {
            targetType
            targetId
            reportId
            display
            latitude
            longitude
            distanceMeters
            status
            reasonCode
        }
    }
`;

export interface WmsPodExemptNearbyResponse {
  wmsPodExemptNearbyEffective: PodExemptNearbyItemType[];
}

// ---------- 审核端 ----------

export const GET_QC_POD_EXEMPT_REPORTS = gql`
    query QcPodExemptReports($filters: PodExemptReportFiltersInput, $pageRequest: PageRequestTypeInput) {
        qcPodExemptReports(filters: $filters, pageRequest: $pageRequest) {
            ${REVIEW_PAGE_FIELDS}
        }
    }
`;

export interface QcPodExemptReportsResponse {
  qcPodExemptReports: PodExemptReportPageType;
}

export const GET_QC_POD_EXEMPT_REVIEW_STATS = gql`
    query QcPodExemptReviewStats {
        qcPodExemptReviewStats {
            pendingCount
            overdueCount
            materialIncompleteCount
            weeklyPassRatePercent
        }
    }
`;

export interface QcPodExemptReviewStatsResponse {
  qcPodExemptReviewStats: PodExemptReviewQueueStatsType;
}

export const GET_QC_POD_EXEMPT_REPORT_DETAIL = gql`
    query QcPodExemptReportDetail($reportId: Long!) {
        qcPodExemptReportDetail(reportId: $reportId) {
            ${POD_EXEMPT_DETAIL_FIELDS}
        }
    }
`;

export interface QcPodExemptReportDetailResponse {
  qcPodExemptReportDetail: PodExemptReportDetailType;
}

export const GET_QC_POD_EXEMPT_NEARBY = gql`
    query QcPodExemptNearbyEffective($reportId: Long!) {
        qcPodExemptNearbyEffective(reportId: $reportId) {
            targetType
            targetId
            reportId
            display
            latitude
            longitude
            distanceMeters
            status
            reasonCode
        }
    }
`;

export interface QcPodExemptNearbyResponse {
  qcPodExemptNearbyEffective: PodExemptNearbyItemType[];
}

/**
 * DSP「抽查记录」页：统计卡 + 列表一次返回。
 * 后端刻意合并为单接口——拆两个 query 会查两遍，两次之间数据变动会出现
 * 「卡片说 12 批次、列表数出来 11 个」的对不上。
 * dspId 由服务端按登录态注入，前端无从传入（数据域隔离键）。
 */
export const GET_POD_EXEMPT_SPOT_TASKS_FOR_DSP = gql`
    query PodExemptSpotTasksForDsp(
        $filters: PodExemptSpotTaskFiltersForDspInput
        $pageRequest: PageRequestTypeInput
        $recentDays: Int
    ) {
        podExemptSpotTasksForDsp(filters: $filters, pageRequest: $pageRequest, recentDays: $recentDays) {
            stats {
                batchCount
                sampleCount
                failRate
                offlineCount
            }
            content {
                id
                taskNo
                periodNo
                periodType
                targetType
                targetId
                targetNo
                targetAddress
                targetZipcode
                targetAddressCount
                status
                sampleCount
                failCount
                assigneeName
                doneAt
                conclusion
                conclusionRemark
                createdAt
            }
            page
            size
            totalElements
            totalPages
            hasNext
            hasPrevious
        }
    }
`;

export interface PodExemptSpotTasksForDspResponse {
  podExemptSpotTasksForDsp: PodExemptDspSpotCheckPageType;
}

/**
 * 历史投递 POD 图片（懒加载专用）。
 *
 * 刻意不放进详情主查询：presign URL 仅 10 分钟有效，提前签发会过期；
 * 且未展开画廊的审核单不该产生图片流量。前端在画廊滚入视口时才发起本查询。
 */
export const GET_WMS_POD_EXEMPT_POD_SAMPLES = gql`
    query WmsPodExemptPodSampleImages($reportId: Long!) {
        wmsPodExemptPodSampleImages(reportId: $reportId) {
            orderId
            driverName
            deliveredAt
            imageUrls
        }
    }
`;

export interface WmsPodExemptPodSamplesData {
  wmsPodExemptPodSampleImages: PodExemptPodSampleType[];
}

export const GET_QC_POD_EXEMPT_POD_SAMPLES = gql`
    query QcPodExemptPodSampleImages($reportId: Long!) {
        qcPodExemptPodSampleImages(reportId: $reportId) {
            orderId
            driverName
            deliveredAt
            imageUrls
        }
    }
`;

export interface QcPodExemptPodSamplesData {
  qcPodExemptPodSampleImages: PodExemptPodSampleType[];
}

/** 点「新建上报」前置校验：当前账号是否已被禁止申报；提交时后端仍会再校验一次 */
export const IS_POD_EXEMPT_REPORTER_BLOCKED = gql`
    query PodExemptReporterBlocked {
        podExemptReporterBlocked
    }
`;

export interface PodExemptReporterBlockedResponse {
  podExemptReporterBlocked: boolean;
}
